import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, X, ZoomIn, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PhotoViewer({ photos = [], open, onClose, title = 'Photos' }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = () => {
    const url = getFullUrl(photos[currentIndex]);
    window.open(url, '_blank');
  };

  if (!photos || photos.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center justify-between">
            <span>{title}</span>
            <span className="text-sm font-normal text-gray-500">
              {currentIndex + 1} / {photos.length}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative bg-gray-900">
          {/* Main Image */}
          <div className="flex items-center justify-center min-h-[400px] max-h-[60vh]">
            <img
              src={getFullUrl(photos[currentIndex])}
              alt={`Photo ${currentIndex + 1}`}
              className="max-w-full max-h-[60vh] object-contain"
            />
          </div>

          {/* Navigation Arrows */}
          {photos.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        {/* Thumbnail Strip */}
        {photos.length > 1 && (
          <div className="p-3 bg-gray-100 flex gap-2 overflow-x-auto">
            {photos.map((photo, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-blue-500 ring-2 ring-blue-200' 
                    : 'border-transparent hover:border-gray-300'
                }`}
              >
                <img
                  src={getFullUrl(photo)}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="p-3 border-t flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline photo thumbnails component for use in tables/cards
export function PhotoThumbnails({ photos = [], maxShow = 3, onViewAll }) {
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (!photos || photos.length === 0) {
    return <span className="text-gray-400 text-sm">No photos</span>;
  }

  const displayPhotos = photos.slice(0, maxShow);
  const remaining = photos.length - maxShow;

  return (
    <div className="flex items-center gap-1">
      {displayPhotos.map((photo, index) => (
        <img
          key={index}
          src={getFullUrl(photo)}
          alt={`Photo ${index + 1}`}
          className="w-8 h-8 rounded object-cover border cursor-pointer hover:ring-2 hover:ring-blue-300"
          onClick={onViewAll}
        />
      ))}
      {remaining > 0 && (
        <button
          onClick={onViewAll}
          className="w-8 h-8 rounded bg-gray-100 border flex items-center justify-center text-xs font-medium text-gray-600 hover:bg-gray-200"
        >
          +{remaining}
        </button>
      )}
    </div>
  );
}
